import { useEffect, useState, type FormEvent } from 'react';
import { Bot, X } from 'lucide-react';
import { api, ApiError, type UserSummary } from '../api/client.ts';
import './NewChatDialog.css';

export function NewChatDialog({
  onClose,
  onStart,
}: {
  onClose: () => void;
  /** Resolves once the conversation is open; rejects with the reason to show. */
  onStart: (nickname: string) => Promise<void>;
}) {
  const [nickname, setNickname] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * The bots, offered under the field.
   *
   * People are still typed rather than listed, for the reason this dialog has
   * always given: a nickname is one person and a typo is obvious. A bot is the
   * case that argument does not cover — its nickname is a machine name somebody
   * chose in the admin panel, Оля has no reason to know it, and adding one is
   * how she subscribes to what it delivers. So the few of them are named here
   * and starting the chat is a tap.
   */
  const [bots, setBots] = useState<UserSummary[]>([]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    // Failing quietly: the field below is the way in, and this is an offer of a
    // shortcut. A deployment with no integrations shows nothing extra at all.
    api
      .listUsers()
      .then((users) => setBots(users.filter((user) => user.bot)))
      .catch(() => setBots([]));
  }, []);

  async function start(name: string) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await onStart(name);
    } catch (err) {
      // An unknown nickname keeps the dialog open with the reason inline.
      setError(err instanceof ApiError ? err.message : 'Не удалось начать чат');
      setBusy(false);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const trimmed = nickname.trim();
    if (!trimmed) return;
    await start(trimmed);
  }

  return (
    <div className="dialog-scrim" onMouseDown={onClose}>
      <form
        className="dialog-card"
        onSubmit={submit}
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Начать чат"
      >
        <button
          type="button"
          className="icon-button dialog-close"
          onClick={onClose}
          aria-label="Закрыть"
        >
          <X size={16} />
        </button>

        <h3 className="dialog-title">Начать чат</h3>

        <label className="field-label" htmlFor="new-chat-nickname">
          Введите никнейм
        </label>
        <input
          id="new-chat-nickname"
          className="text-field dialog-field"
          value={nickname}
          onChange={(event) => setNickname(event.target.value)}
          autoCapitalize="none"
          autoFocus
        />

        <button className="primary-button" type="submit" disabled={busy}>
          Начать
        </button>

        {bots.length > 0 && (
          <div className="dialog-bots">
            <div className="dialog-bots-label">Боты</div>
            {bots.map((bot) => (
              <button
                key={bot.id}
                type="button"
                className="dialog-bot"
                disabled={busy}
                onClick={() => void start(bot.username)}
              >
                <Bot size={16} />
                <span className="dialog-bot-name">{bot.displayName}</span>
                <span className="dialog-bot-nick">@{bot.username}</span>
              </button>
            ))}
            <p className="dialog-bots-hint">
              Добавьте — и то, что бот присылает, будет приходить вам.
            </p>
          </div>
        )}

        {error && <div className="form-error">{error}</div>}
      </form>
    </div>
  );
}
